import {
  IKyc1LevelStatus,
  IKyc2LevelStatus,
  IKycStatusV2,
  type IOptionItem,
  type TKycInfoV2,
} from "@adgm/cross-models";
import { userCountriesData } from "@aspen/assets/offlineData/usersCountries";
import { set } from "lodash-es";
import { USER } from "../constant";
import { IS_WEB } from "./common";

/**
 * 对象属性复制工具函数,用来给kyc表单复制已有数据(表单缓存或者kyc-get属于)使用,暂不提供其他地方使用
 * 将源对象中的属性值复制到目标对象中，只复制目标对象中存在的属性
 * 支持嵌套对象的递归复制，跳过 undefined 值，保留 null、false 等有效值
 * 特殊处理：当源对象是空对象 {} 且目标对象是叶子对象（所有属性都是基本类型）时，直接替换整个对象
 * 备注:暂时使用kyc表单填充默认数据使用
 *
 * @param formData 目标对象，将被源对象的属性值填充
 * @param kycInfo 源对象，提供要复制的属性值
 *
 * @example
 * ```typescript
 * // 基本复制示例
 * const target = { name: "", age: 0 };
 * const source = { name: "John", age: 25, email: "john@example.com" };
 * transformData(target, source);
 * // target 变为 { name: "John", age: 25 }
 *
 * // 空对象替换示例
 * const target = {
 *   operations: {
 *     listedEntity: { name: "old", country: "old" }
 *   }
 * };
 * const source = {
 *   operations: {
 *     listedEntity: {}
 *   }
 * };
 * transformData(target, source);
 * // target.operations.listedEntity 变为 {}
 *
 * // 嵌套对象递归示例
 * const target = {
 *   user: {
 *     profile: { name: "", age: 0 }
 *   }
 * };
 * const source = {
 *   user: {
 *     profile: { name: "John", age: 25 }
 *   }
 * };
 * transformData(target, source);
 * // target.user.profile 变为 { name: "John", age: 25 }
 * ```
 */
export const transformData = <T extends Record<string, any>, U extends Record<string, any>>(
  formData: T,
  kycInfo: U | undefined | null,
): void => {
  if (!kycInfo) return;

  const processNestedFields = (obj: any, sourceObj: any, prefix = "") => {
    Object.keys(obj).forEach(key => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        // 递归处理嵌套对象
        const sourceValue = sourceObj[key];
        if (sourceValue && typeof sourceValue === "object") {
          // 如果源对象是空对象，且目标对象也是叶子对象（没有更深层嵌套），则直接替换
          if (
            Object.keys(sourceValue).length === 0 &&
            Object.keys(value).length > 0 &&
            Object.values(value).every(v => typeof v !== "object" || v === null || Array.isArray(v))
          ) {
            set(formData, fullPath, sourceValue);
          } else {
            processNestedFields(value, sourceValue, fullPath);
          }
        }
      } else {
        // 处理叶子节点 - 从对应的源对象中获取值
        const sourceValue = sourceObj[key];
        if (sourceValue !== undefined) {
          set(formData, fullPath, sourceValue);
        }
      }
    });
  };

  processNestedFields(formData, kycInfo);
};

// KYC国家选择选项列表
export const kycCountrySelectOptionList = (type: "commonName" | "nationality"): IOptionItem[] =>
  userCountriesData.map(item => ({
    title: `${item.nationalFlag} ${
      type === "commonName" ? item?.commonName : item?.nationality
    }${type === "commonName" ? `(${item?.id})` : ""}`,
    value: item.id,
    commonName: item?.commonName,
    countryCode: item?.countryCode,
  }));

/**
 * @description: 将后端返回的kycInfo的status转换为前端需要的status
 * 状态映射规则：
 * - 后端 init(0) -> 前端 IKyc1LevelStatus.init(0) - 未认证状态
 * - 后端 Auditing(7) -> 前端 IKyc2LevelStatus.Auditing(4) - 审核中状态
 * - 后端 Certified(8) -> 前端 IKyc2LevelStatus.Certified(6) - 已认证状态
 * - 后端 Rejected(9) -> 前端 IKyc2LevelStatus.Rejected(5) - 审核失败状态
 *
 * @param status - KYC状态 (IKycStatusV2枚举值)
 * @return {IKyc1LevelStatus | IKyc2LevelStatus} 转换后的等级状态值
 *
 */
export const convertKycStatus = (status: IKycStatusV2): IKyc1LevelStatus | IKyc2LevelStatus => {
  switch (status) {
    case IKycStatusV2.init:
      return IKyc1LevelStatus.init;
    case IKycStatusV2.Auditing:
      return IKyc2LevelStatus.Auditing;
    case IKycStatusV2.Certified:
      return IKyc2LevelStatus.Certified;
    case IKycStatusV2.Rejected:
      return IKyc2LevelStatus.Rejected;
    default:
      return status;
  }
};

/**
 * @description: 将后端返回的KYC信息转换为前端需要的格式
 * 状态映射规则：
 * - 后端 init(0) -> 前端 IKyc1LevelStatus.init(0) - 未认证状态
 * - 后端 Auditing(7) -> 前端 IKyc2LevelStatus.Auditing(4) - 审核中状态
 * - 后端 Certified(8) -> 前端 IKyc2LevelStatus.Certified(6) - 已认证状态
 * - 后端 Rejected(9) -> 前端 IKyc2LevelStatus.Rejected(5) - 审核失败状态
 *
 * 字段映射规则：
 * - email: Web端从sessionStorage获取，App端从global.accountName获取
 * - remark: 从kycInfo.reviewReason获取
 * - nationality: 从kycInfo.nationality获取
 * - companyRegistrationCountry: 从kycInfo.institution.operations.operatingCountry获取
 *
 * @param kycInfo - 后端返回的KYC信息对象
 * @return {TKycInfoV2} 转换后的前端KYC信息对象
 *
 */
export const convertKycInfoToFrontend = (
  kycInfo: Omit<TKycInfoV2, "status"> & { status: IKycStatusV2 },
): TKycInfoV2 => {
  const { status, ...rest } = kycInfo;

  return {
    ...rest,
    name: kycInfo.name ?? "",
    status: convertKycStatus(status),
    email: IS_WEB ? sessionStorage.getItem(USER) : (global.accountName ?? ""),
    remark: kycInfo.reviewReason ?? "",
    nationality: kycInfo.nationality ?? "",
    companyRegistrationCountry: kycInfo.institution?.operations?.operatingCountry ?? "",
    countryCode: kycInfo.countryCode ?? "",
  };
};

/**
 * 将字符串数组转换为选项对象数组，用于Select、Radio、Checkbox等UI组件
 * @param options - 字符串数组
 * @return 选项对象数组，格式: [{title: string, value: string}]
 * @example createOptions(['男', '女']) => [{title: '男', value: '男'}, {title: '女', value: '女'}]
 */
export const createOptions = (options: string[]) =>
  options.map(option => ({ title: option, value: option }));
